import './style.css';

import { FC, useState } from 'react';
import Box from '@mui/material/Box';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Typography from '@mui/material/Typography';
import ChartTabs from './chart-tabs';
import LayerDetails from './details';
import { IResult, IResultLayer, IResultLayerClass } from '../../core/interfaces/result.interface';
import { IconButton, Switch, Tooltip } from '@mui/material';
import { Download, Share, SpeakerNotesOutlined } from '@mui/icons-material';
import ResultSharingComponent from '../ResultSharing';
import { IBiodiversityUnit } from '../../core/interfaces/BNG-units.interface';
import { sortLayerClasses } from '../../Utils/layer';
import { IProject } from '../../core/interfaces/project.interface';

interface LayersComponentProps {
  result: IResult;
  project?: IProject;
  onCheckboxChange?: (checked: boolean, classItem: { id: string; classItem: IResultLayerClass }) => void;
  onSelect?: (selected: boolean, layer: IResultLayer) => void;
  notesColumnOpen?: boolean;
  setNotesColumnOpen?: (open: boolean) => void;
  units?: IBiodiversityUnit;
  onDownloadButton?: () => void;
  productType?: string;
}

const LayersComponent: FC<LayersComponentProps> = ({
  result,
  project,
  onCheckboxChange,
  onSelect,
  notesColumnOpen,
  setNotesColumnOpen,
  onDownloadButton,
  productType,
  units,
}) => {
  const { layers } = result || ({ layers: [] } as IResult);
  const [shareLinkModalOpen, setShareLinkModalModalOpen] = useState(false);

  const handleNotesColumnToggle = () => {
    if (setNotesColumnOpen) {
      setNotesColumnOpen(!notesColumnOpen);
    } else {
      console.error('setNotesColumnOpen is not defined');
    }
  };

  // Sort the classes array in descending order of dimension values
  const sortedLayers = layers.map((layer) => sortLayerClasses(layer));

  // Combine ChartTabs and BNGTabs data into a single array
  const combinedTabsData = [
    ...sortedLayers
      .filter((layer) => layer.tab?.visible)
      .map((layer) => ({ ...layer.tab, type: 'chart' as const })), // Chart tabs
  ];

  const biodiversityUnits =
    result?.shapes?.reduce((obj, shape) => {
      if (!shape.data?.stats?.habitat_stats || !shape.data?.stats?.total_stats) {
        return obj;
      }
      const habitatGroup = Object.values(shape.data.stats.habitat_stats)[0]?.HabGroup;
      if (habitatGroup) {
        if (habitatGroup === 'Hedgerow') {
          obj['hedgerowUnits'] = shape.data.stats.total_stats['Total Biodiversity Units'];
        }

        if (habitatGroup === 'Watercourse') {
          obj['watercourseUnits'] = shape.data.stats.total_stats['Total Biodiversity Units'];
        }
      } else {
        obj['habitatUnits'] = shape.data.stats.total_stats['Total Biodiversity Units'];
      }
      return obj;
    }, {} as IBiodiversityUnit) ?? ({} as IBiodiversityUnit);
  const hasProjectBNG =
    !!project &&
    (project.habitatUnits != null ||
      project.hedgerowUnits != null ||
      project.watercourseUnits != null ||
      project.totalHedgerowLength != null ||
      project.totalWatercourseLength != null);
  const hasUnits =
    !!units ||
    biodiversityUnits.habitatUnits != null ||
    biodiversityUnits.hedgerowUnits != null ||
    biodiversityUnits.watercourseUnits != null ||
    biodiversityUnits.totalHedgerowLength != null ||
    biodiversityUnits.totalWatercourseLength != null;
  const effectiveBngTabEnabled = hasProjectBNG || hasUnits;

  return (
    <Box
      sx={{
        maxWidth: '320px',
        minWidth: '320px',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: '#fff',
        paddingTop: '8px',
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', px: '8px' }}>
        <Typography variant="body1" sx={{ pr: '4px' }}>
          <b>{project?.name}</b>
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <IconButton
            sx={{ display: result?.status === 'published' ? 'inline-flex' : 'none' }}
            size="medium"
            onClick={() => setShareLinkModalModalOpen(true)}
          >
            <Tooltip title="Share result">
              <Share />
            </Tooltip>
          </IconButton>
          <IconButton
            sx={{ display: result?.status === 'published' ? 'inline-flex' : 'none' }}
            size="medium"
            onClick={handleNotesColumnToggle}
          >
            <Tooltip title="Notes">
              <SpeakerNotesOutlined />
            </Tooltip>
          </IconButton>
          {productType === 'True Sight' && (
            <IconButton
              sx={{ display: result?.status === 'published' ? 'inline-flex' : 'none' }}
              size="medium"
              onClick={onDownloadButton}
            >
              <Tooltip title="Download files">
                <Download />
              </Tooltip>
            </IconButton>
          )}
          {!!result?.deliverableUrl && (
            <IconButton size="medium" onClick={() => window.open(result.deliverableUrl, '_blank')}>
              <Tooltip title="Download">
                <Download />
              </Tooltip>
            </IconButton>
          )}
        </Box>
        {shareLinkModalOpen && (
          <ResultSharingComponent
            link={`${window.location.origin}/survey-result/${result?.project?._id}`}
            onClose={() => setShareLinkModalModalOpen(false)}
            projectId={result?.project?._id}
            hasDownloadLink={!!result?.deliverableUrl}
          />
        )}
      </Box>
      {/* Combined Tabs */}
      <ChartTabs
        chartData={combinedTabsData}
        units={biodiversityUnits}
        productType={productType}
        project={project}
      />
      {sortedLayers.map(
        (layer, index) =>
          layer.details.visible && (
            <Accordion key={`${layer.resultId}_${layer.displayName}`}>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls={`panel${index}-content`}
                id={`panel${index}-header`}
                sx={{ '& .MuiAccordionSummary-content': { margin: '0px' } }}
              >
                <Switch
                  checked={layer.details.defaultEnabled !== false} // also check for undefined for retrocompatibility
                  onClick={(e) => {
                    e.stopPropagation();
                    const checked = (e.target as HTMLInputElement).checked;
                    onSelect?.(checked, layer);
                  }}
                ></Switch>
                <Typography sx={{ lineHeight: '38px' }}>{layer?.displayName}</Typography>
              </AccordionSummary>
              <AccordionDetails
                sx={{ m: 0, pl: 1 }}
                hidden={layer.details.defaultEnabled === false || !layer?.details?.classes?.length} // also check for undefined for retrocompatibility
              >
                <LayerDetails layer={layer} onCheckboxChange={onCheckboxChange} />
              </AccordionDetails>
            </Accordion>
          ),
      )}
    </Box>
  );
};

export default LayersComponent;
